import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  generatePath,
  useOutletContext,
} from 'react-router-dom';
import { useForm, Control } from 'react-hook-form';
import { SendMessage } from 'react-use-websocket';
import {
  BaseTransaction,
  BenificiaryType,
  OutgoingSidepanel,
  ResponseError,
  responseErrorsResolver,
  ajvResolver,
  PermissionEnum,
  BaseellaStatus,
  formatBalance,
  usePrevious,
  ChargeType,
  PaymentType,
  SelectItemProps,
} from '@ui/src';
import { Balance } from '@ui/src/components/transactions-sidepanels/types';
import { useAppSelector, useAppDispatch } from '@store';
import queryString from 'query-string';
import useGetPrimaryAccountsAutocompleteList from '../../store/dictionaries/helpers/use-primary-accounts-autocomplete';
import useGetAccountsAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import useGetPspPartnersAutocompleteList from '../../store/dictionaries/helpers/use-psp-partners-autocomplete';
import { clearStatusesState } from '../../store/statuses/slice';
import {
  changePanelState,
  clearOutgoingTransactionState,
} from './store/slices/transaction';
import { setCollapseAllRows } from './store/slices/list';
import ROUTES from '../../routes';
import {
  useGetOutgoingTransactionValidatorsQuery,
  useUpdateOutgoingTransactionMutation,
} from './store/services';
import { CreateOutgoingTransaction, OugointTransactionTypes } from './types';
import StatusesUrl from '../../types/statuses-url';
import { CheckTransactionStatus } from '../../types/check-transaction';
import {
  clearState,
  clearExpandedGeneralLedgerTransactionState,
} from '../transactions/store/slice';
import { useLazyGetTransactionsAccountBalanceQuery } from '../../store/dictionaries/services';
import { useLazyGetAllAccountalancesQuery } from '../transactions/store/services';
import { MfaToken, MfaType, PurposeCodeSettings } from '../../store/types';
import {
  ibanRegexp,
  // sortCodeRegexp,
  swiftRegexp,
} from '../transactions/helpers/regex';
import { clearSignaturesState } from '../../store/signature/slice';
import useBaseRequests from '../transactions/hooks/use-base-requests';
import useRequests from './hooks/use-request';
import useGetExternalAccountsList from '../incoming/helpers/use-get-external-accounts-list';
import usePurposeCodesList from '../../store/dictionaries/helpers/use-purpose-codes-list';

const defaultFormValues: any = {
  acceptedOut: '',
  benificiaryAddress: '',
  benificiaryBIC: '',
  benificiaryBankAddress: '',
  benificiaryBankCountry: '',
  benificiaryBankName: '',
  benificiaryCountry: '',
  benificiaryFullName: '',
  benificiaryType: '',
  benificiaryBankSortCode: '',
  chargeType: ChargeType.SHA,
  currencyId: '',
  documentNumber: '',
  info: '',
  paymentType: '',
  pspPartnerAccountNumber: '',
  pspPartnerId: '',
  senderAccountNumber: '',
  sum: '',
  transferDate: '',
};

const creationSchema = {
  type: 'object',
  properties: {
    sum: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Amount is required',
    },
    currencyId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Currency is required',
    },
    paymentType: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Payment type is required',
    },
    // pspPartnerId: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Bank partner is required',
    // },
    // pspPartnerAccountNumber: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Bank partner account is required',
    // },
    senderAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Sender account is required',
    },
    benificiaryFullName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Full Name is required',
    },
    // benificiaryAddress: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Address is required',
    // },
    // benificiaryBankName: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Bank name is required',
    // },
    // benificiaryBankCountry: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Bank country is required',
    // },
    // benificiaryBankAddress: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Bank address is required',
    // },
    info: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Info is required',
    },
  },
  required: [
    'sum',
    'currencyId',
    'paymentType',
    // 'pspPartnerId',
    // 'pspPartnerAccountNumber',
    'senderAccountNumber',
    'benificiaryFullName',
    // 'benificiaryAddress',
    // 'benificiaryBankName',
    // 'benificiaryBankCountry',
    // 'benificiaryBankAddress',
    'info',
  ],
};

const phoneRegex =
  /^[\\+]?[0-9]{0,3}\W?[(]?[0-9]{3}[)]?[-\s\\.]?[0-9]{3}[-\s\\.]?[0-9]{4,6}$/im;

const formatFormDate = (date: string) => {
  const dateFormatted = new Date(date);

  dateFormatted.setHours(0);
  dateFormatted.setMinutes(0);
  dateFormatted.setSeconds(0);
  dateFormatted.setMilliseconds(0);

  return dateFormatted;
};

const OutgoingTransaction = ({
  withBackLink,
}: OugointTransactionTypes): React.JSX.Element => {
  const {
    transactions: {
      employee,
      currentEmployee,
      chats,
      companyOfficials,
      document: documentItem,
      documents,
      documentsPagination,
      expandedGLTransaction,
      expandedGLTransactionCreditBalance,
      expandedGLTransactionDebitBalance,
      balances,
      caspList,
      transactionChangeLogs,
      transactionChangeLogsPagination,
      transactionComments,
      transactionCommentsPagination,
    },
    outgoing: {
      transaction: {
        panelIsOpen,
        transaction,
        aml,
        warnings,
        glTransactions,
        warningsInfo,
        integrationLogs,
        integrationLogsPagination,
      },
      list: { selectedCount },
    },
    transactionsDictionaries: {
      currencies,
      countries,
      chargeTypes,
      activePermissions,
      paymentTypes,
      transactionsTypes,
      baseCurrency,
      currenciesList: dictionariesCurrencies,
      overdraftEnabled,
      countriesList,
      purposeCodesSettings,
    },
    settings: {
      details: { pspDetails },
    },
    statuses: { statuses, statusLogs },
    signatures: { signatures },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { ffSettings } = useAppSelector((state) => state.root);

  const [accountBalance, setAccountBalance] = useState<number>();
  const [pspPartnerAccountBalance, setPspPartnerAccountBalance] =
    useState<number>();
  const [pspPartnerAccountBalanceInfo, setPspPartnerAccountBalanceInfo] =
    useState<Balance>();
  const [createdTransactionId, setCreatedTransactionId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [checkError, setCheckError] = useState('');
  const [statusCheckError, setStatusCheckError] = useState('');
  const [amount, setAmount] = useState<string | undefined>();
  const [formattedSchema, setFormattedSchema] = useState(creationSchema);
  const [backPath, setBackPath] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState<
    SelectItemProps[]
  >([]);
  const [filteredPaymentTypes, setFilteredPaymentTypes] = useState<
    SelectItemProps[]
  >([]);
  const [statusChangeError, setStatusChangeError] = useState('');
  const [purposeCodeSettingItem, setPurposeCodeSettingItem] = useState<
    PurposeCodeSettings | undefined
  >(undefined);
  const [purposeCodeSettingSelectionType, setPurposeCodeSettingSelectionType] =
    useState('');
  const [warningActionError, setWarningActionError] = useState<
    { warningId: string; error: string } | undefined
  >(undefined);

  useGetOutgoingTransactionValidatorsQuery();

  const { sendMessage } = useOutletContext<{ sendMessage: SendMessage }>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { bankId, accountId, batchId, transactionId } = useParams();

  const { search, state: locationState } = useLocation();

  const {
    control,
    formState: { dirtyFields, errors },
    reset,
    setError,
    getValues,
    watch,
    setValue,
    clearErrors,
    handleSubmit,
  } = useForm({
    resolver: ajvResolver(formattedSchema),
    defaultValues: {
      ...defaultFormValues,
    },
  });

  const fields = watch();
  const pspPartnerId = watch('pspPartnerId');
  const paymentType = watch('paymentType');
  const currencyId = watch('currencyId');
  const beneficiaryAccount = watch('senderAccountNumber');
  const pspPartnerAccountNumber = watch('pspPartnerAccountNumber');
  const acceptedOut = watch('acceptedOut');
  const transferDate = watch('transferDate');
  const benificiaryBankCountry = watch('benificiaryBankCountry');
  const intermediaryBankCountry = watch('intermediaryBankCountry');
  const pspIntegrationAccountId = watch('pspIntegrationAccountId');

  const prevCurrencyId = usePrevious(currencyId);
  const prevPaymentType = usePrevious(paymentType);
  const prevPspPartnerId = usePrevious(pspPartnerId);
  const prevBenificiaryBankCountry = usePrevious(benificiaryBankCountry);

  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);

  const isDirty = Object.keys(dirtyFields).length !== 0;

  const [
    getAllAccountBalances,
    {
      isLoading: allAccountBalancesIsLoading,
      isFetching: allAccountBalancesisFetching,
    },
  ] = useLazyGetAllAccountalancesQuery();

  const [
    getAccountBalance,
    {
      isLoading: accountBalanceIsLoading,
      isFetching: accountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();
  const [
    getPspPartnerAccountBalance,
    {
      isLoading: pspPartnerAccountBalanceIsLoading,
      isFetching: pspPartnerAccountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();

  const [refund, { isLoading: refundInProgress, isSuccess: refundIsSuccess }] =
    useUpdateOutgoingTransactionMutation();

  const setCollapseAllRowsAction = () => {
    dispatch(setCollapseAllRows(true));
  };

  const onClearSidepanel = () => {
    dispatch(clearOutgoingTransactionState());
    dispatch(clearState());
    dispatch(clearStatusesState());
    dispatch(clearSignaturesState());
  };

  const {
    feeValue,
    setFeeValue,
    submitDisabled,
    setSubmitDisabled,
    checkStatus,
    isLoading,
    isUninitialized,
    updateIsLoading,
    updateIsError,
    create,
    createIsSuccess,
    createIsLoading,
    getTransactionsList,
    isWarningLoading,
    isWarningFetching,
    isLoadingAml,
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    applyTransactionsWarningIsLoading,
    onClearTimer,
    onSubmit,
    getOutgoingTransactionByIdAmlAction,
    onApplyTransactionAction,
    checkInProgress,
    getTransaction,
    getStatusesTransactionById,
    refundPopupVisible,
    setRefundPopupVisible,
    onChangeRefundState,
    refundFormState,
    refundAmountError,
    setRefundAmountError,
    onResetTransactionWarningAction,
    resetTransactionsWarningIsLoading,
    resetTransactionsWarningIsSuccess,
    getOutgoingTransactionWarningByIdAction,
    isWarningByIdFetching,
    isWarningByIdLoading,
    isWarningInfoLoading,
    onValidateIban,
    ibanValidationValues,
    ibanValidationIsLoading,
    onSearchBic,
    onBicValidate,
    bicSearchIsLoading,
    beneficairyBicSearchResults,
    intermediaryBicSearchResults,
    selectedValidationType,
    bicValidationIntermediaryResult,
    bicValidationBeneficiaryResult,
    bicValidationIsLoading,
    setBicValidationBeneficiaryResult,
    setIbanValidationValues,
    selectedWarningId,
    bicRequestForbidden,
    ibanRequestForbidden,
    getIntegrationLogs,
    integrationLogsIsLoading,
    integrationLogsIsFetching,
    transferDateWithTime,
    acceptedOutWithTime,
    setTransferDateWithTime,
    setAcceptedOutWithTime,
  } = useRequests({
    setErrorMessage,
    setStatusChangeError,
    getValues,
    transactionId,
    setError,
    transaction,
    errors,
    activePermissions,
    warnings,
    setCheckError,
    fields,
    panelIsOpen,
    isDirty,
    setStatusCheckError,
    clearErrors,
    setValue,
    benificiaryBankCountry,
    intermediaryBankCountry,
    pspDetails,
    purposeCodeSettingItem,
    purposeCodeSettingSelectionType,
    setWarningActionError,
  });

  const handleClose = useCallback(() => {
    dispatch(clearOutgoingTransactionState());
    dispatch(clearState());
    setUploadedFilesError([]);
    dispatch(clearSignaturesState());

    localStorage.removeItem('outgoingNoScheme');

    onClearTimer(true);

    setTimeout(() => {
      if (withBackLink) {
        if (batchId) {
          navigate(
            generatePath(ROUTES.EXPORT_BATCHES_READ, {
              bankId: bankId as string,
              accountId: accountId as string,
              batchId,
            }),
          );
        } else {
          navigate(
            generatePath(ROUTES.EXPORT_BATCHES_ADD, {
              bankId: bankId as string,
              accountId: accountId as string,
            }),
          );
        }
      } else if (backPath) {
        navigate(backPath);
      } else {
        navigate(`${ROUTES.OUTGOING}${search || ''}`);
        setCheckError('');
      }

      setBackPath('');
    }, 100);
  }, [backPath, search]);

  const {
    imgLink,
    deleteTransactionError,
    errorMessageSignature,
    setErrorMessageSignature,
    signaturePopupVisible,
    setSignaturePopupVisible,
    statusChangeInProgress,
    statusPopupVisible,
    setStatusPopupVisible,
    rejectPopupVisible,
    setRejectPopupVisible,
    deleteTransactionInProgress,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    editCommentInProgress,
    editCommentIsSuccess,
    deleteCommentInProgress,
    commentInProgress,
    documentCategoryChangeInProgress,
    documentStatusChangeInProgress,
    documentIsLoading,
    documentIsFetching,
    downloadInProgress,
    downloadIsFetching,
    downloadFileIsSuccess,
    documentsIsLoading,
    addDocumentInProgress,
    fileUploadIsSuccess,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    glTransactionIsLoading,
    glTransactionIsFetching,
    glIsLoading,
    fileUploadInProgress,
    transactionChatsIsLoading,
    cofIsLoading,
    chatCreateInProgress,
    isLoadingSignatures,
    createSignatureInProgress,
    signatureIsSuccess,
    sendCodeInProgress,
    validateCodeInProgress,
    changeStatusIsSuccess,
    changeStatusIsLoading,
    isLoadingLogs,
    onDeleteTransaction,
    getTransactionDocumentyIdAction,
    onGlTransactionClick,
    onUploadDocument,
    onCreateTransactionChat,
    createChatWithMessage,
    onSendCode,
    validateCodeAction,
    getLogsTransactionByIdAction,
    onClearRequestTimer,
    onChangeStatusPopup,
    onSignatureCancelClick,
    onChangeStatus,
    getTransationDocumentsAction,
    getGeneralLedgerTransactionValuesAction,
    getTransactionsChatsAction,
    getCompanyOfficialsAction,
    getSignaturesTransactionByIdAction,
    onReject,
    onDeleteDocument,
    onMoveDocument,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    onDeleteComment,
    onChangeCustomerDocumentsStatus,
    sendCodeRetry,
    sendCode,
    onClearRequestTimeout,
    getTransactionChangeLogsAction,
    changeLogsIsFetching,
    changeLogsIsLoading,
    onRefund: changeStatusToRefund,
    needToCloseSidepanel,
    documentsIsFetching,
    attachedDocumentsErrors,
    setAttachedDocumentsErrors,
    uploadedDocumentsErrors,
    setUploadedDocumentsErrors,
    getTransactionCommentsAction,
    commentsIsFetching,
    commentsIsLoading,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    deleteCommentInTranscationIsSuccess,
  } = useBaseRequests({
    setError,
    transactionId,
    transaction,
    currentEmployee,
    sendMessage,
    setErrorMessage,
    createdTransactionId,
    statusUrl: StatusesUrl.OUTGOING,
    setStatusChangeError,
    onSubmit,
    getTransactionsList,
    getStatusesTransactionById,
    onClearTimer,
    getTransaction,
    reset,
    handleClose,
    onClear: onClearSidepanel,
    chatTitle: 'RFI: Outgoing transaction',
    takeCount: selectedCount,
    search,
    transactionListUrl: ROUTES.OUTGOING_TRANSACTION,
    errors,
    isDirty,
    documents,
    documentItem,
    setCollapseAllRows: setCollapseAllRowsAction,
    updateIsError,
    paymentType,
    expandedGLTransaction,
    setUploadedFilesError,
    setRefundPopupVisible,
    transactionUrl: 'outgoing',
  });

  const onRefund = async () => {
    const response = (await refund({
      body: {
        ...refundFormState,
        refundAmount: Number(refundFormState.refundAmount),
      },
      id: transaction?.id as string,
    })) as { data?: BaseTransaction; error?: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setRefundAmountError,
      });
    }
  };

  const prevSignaturePopupVisible = usePrevious(signaturePopupVisible);

  const sendCodeAction = handleSubmit(async (values) => {
    let createResponse;
    let requestAvailable = true;

    if (values.paymentType === PaymentType.CASH_OVER_COUNTER) {
      if (!values.collectorContact) {
        requestAvailable = false;
        setError('collectorContact', {
          type: 'custom',
          message: 'Collector Contact Is Required',
        });
      } else {
        clearErrors(['collectorContact']);

        if (!phoneRegex.test(values.collectorContact.replace(/\s/g, ''))) {
          requestAvailable = false;

          setError('collectorContact', {
            type: 'custom',
            message: 'Invalid Phone Number',
          });
        } else {
          clearErrors(['collectorContact']);

          requestAvailable = true;
        }
      }
    }

    if (!transactionId && !Object.keys(errors).length && requestAvailable) {
      const body: CreateOutgoingTransaction = {
        chargeType: values.chargeType,
        disableExternalTransactionCreate:
          values?.disableExternalTransactionCreate,
      };

      if (values?.pspIntegrationAccountId) {
        body.pspIntegrationAccountId = values.pspIntegrationAccountId;
      }

      if (values?.blockchainTransactionHashId) {
        body.blockchainTransactionHashId = values.blockchainTransactionHashId;
      }

      if (values.recipientAccountNumber) {
        body.recipientAccountNumber = values.recipientAccountNumber;
      }

      if (values.benificiaryFullName) {
        body.benificiaryFullName = values.benificiaryFullName;
      }

      if (values.benificiaryBankCountry) {
        body.benificiaryBankCountry = values.benificiaryBankCountry;
      }

      if (values.benificiaryBankAddress) {
        body.benificiaryBankAddress = values.benificiaryBankAddress;
      }

      if (values.intermediaryBankName) {
        body.intermediaryBankName = values.intermediaryBankName;
      }

      if (values.intermediaryBankCountry) {
        body.intermediaryBankCountry = values.intermediaryBankCountry;
      }

      if (values.intermediaryBankAddress) {
        body.intermediaryBankAddress = values.intermediaryBankAddress;
      }

      if (values.benificiaryBankSortCode) {
        body.benificiaryBankSortCode = values.benificiaryBankSortCode;
      }

      if (values.intermediaryBankCorrespondentAccount) {
        body.intermediaryBankCorrespondentAccount =
          values.intermediaryBankCorrespondentAccount;
      }

      if (values.intermediaryBankBIC) {
        body.intermediaryBankBIC = values.intermediaryBankBIC;
      }

      if (values.benificiaryAddress) {
        body.benificiaryAddress = values.benificiaryAddress;
      }

      if (values.benificiaryBIC) {
        body.benificiaryBIC = values.benificiaryBIC;
      }

      if (values.benificiaryBankName) {
        body.benificiaryBankName = values.benificiaryBankName;
      }

      if (values.benificiaryType) {
        body.benificiaryType = values.benificiaryType;
      }

      if (values.benificiaryCountry) {
        body.benificiaryCountry = values.benificiaryCountry;
      }

      if (values.pspPartnerAccountNumber) {
        body.pspPartnerAccountNumber = values.pspPartnerAccountNumber;
      }

      if (values.documentNumber) {
        body.documentNumber = values.documentNumber;
      }

      if (values.paymentType) {
        body.paymentType = values.paymentType;
      }

      if (values.info) {
        body.info = values.info;
      }

      if (values.collectorContact) {
        body.collectorContact = values.collectorContact;
      }

      if (values.customPassword) {
        body.customPassword = values.customPassword;
      }

      if (values.aba) {
        body.aba = values.aba;
      }

      if (values.transferDate) {
        const date = new Date(values.transferDate);

        if (transferDateWithTime) {
          body.transferDate = date.toUTCString();
        } else {
          const currentDate = new Date();
          const hours = currentDate.getHours();
          const minutes = currentDate.getMinutes();
          const seconds = currentDate.getSeconds();

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(seconds);

          body.transferDate = date.toISOString();
        }
      }

      if (values.currencyId) {
        body.currencyId = values.currencyId;
      }

      if (values.sum) {
        body.sum = Number(values.sum);
      }

      if (values.senderAccountNumber) {
        body.senderAccountNumber = values.senderAccountNumber;
      }

      if (values.pspPartnerId) {
        body.pspPartnerId = values.pspPartnerId;
      }

      if (values.benificiaryCity) {
        body.benificiaryCity = values.benificiaryCity;
      }

      if (values?.caspId) {
        body.caspId = values.caspId;
      }

      if (values.purposeCode) {
        body.purposeCode = values.purposeCode;
      }

      if (values.benificiaryPostCode) {
        body.benificiaryPostCode = values.benificiaryPostCode;
      }

      createResponse = (await create(body)) as {
        error: ResponseError;
        data?: {
          data: BaseTransaction;
        };
      };
    }

    if (createResponse && createResponse.error) {
      responseErrorsResolver({
        errors: createResponse.error,
        setError,
        setGlobalError: setErrorMessage,
      });
    } else if (createResponse && createResponse.data) {
      if (createResponse && createResponse.data) {
        setCreatedTransactionId(createResponse.data.data.id);
      }

      if (currentEmployee?.canSignTransactions) {
        const response = (await sendCode({ mfa: MfaType.TOTP })) as {
          data: { data: MfaToken };
        };

        if (response && response.data && response.data.data) {
          setSignaturePopupVisible(true);

          const expireTime = Math.round(
            new Date(response.data.data.expiresAt).getTime() - Date.now(),
          );

          sendCodeRetry(expireTime);
        }
      }
    }
  });

  const {
    autocompleteList: senderAccounts,
    onSearch: onSearchSenderAccounts,
    isLoading: senderAccountsIsLoading,
    accountsList,
    setAutocompleteList,
    setAccountsList,
  } = useGetAccountsAutocompleteList(
    `&isClientAccount=true${!transactionId ? '&closed=false' : ''}`,
    'fullNumber',
  );

  const {
    autocompleteList: pspPartners,
    onSearch: onSearchPspPartners,
    isLoading: pspPartnersIsLoading,
    getAutocompleteListAction,
  } = useGetPspPartnersAutocompleteList(
    paymentType ? `&paymentsTypes=${paymentType}` : '',
  );

  const {
    externalAccountsList,
    onSearch: onSearchExternalAccounts,
    onClear: onClearExxternalAccounts,
    getExternalAccountsListAction,
    isLoading: externalAccountsListLoading,
    externalAccountsPagination,
    nextPageIsLoading: externalAccountsNextPageIsLoading,
    setExternalAccountsList,
    setExternalAccountsPagination,
  } = useGetExternalAccountsList();

  const {
    autocompleteList: pspPartnerAccounts,
    onSearch: onSearchPspPartnerAccounts,
    isLoading: pspPartnerAccountsIsLoading,
    accountsList: pspPartnersAccountsList,
    nextPageIsLoading,
    onScroll,
    pagination,
    getTransactionPspAccountByIdAction,
  } = useGetPrimaryAccountsAutocompleteList(pspPartnerId, currencyId);

  const {
    autocompleteList: purposeCodes,
    onSearch: onSearchPurposeCodes,
    isLoading: purposeCodesIsLoading,
    nextPageIsLoading: purposeCodesNextPageIsLoading,
    onScroll: onScrollPurposeCodes,
    pagination: purposeCodesPagination,
    getPurposeCodeByCode,
  } = usePurposeCodesList(purposeCodeSettingItem?.purposeCodeType);

  const getAllAccountBalancesAction = useCallback(
    (id: string) => {
      getAllAccountBalances({
        id,
      });
    },
    [beneficiaryAccount],
  );

  const getAccountBalanceAction = useCallback(
    async (number?: string) => {
      const currentAccount = accountsList?.find(
        (item) => item.fullNumber === beneficiaryAccount || number,
      );

      if (currentAccount && currencyId) {
        const response = (await getAccountBalance({
          id: currentAccount.id,
          currencyId,
        })) as { data: { data: { edges: { node: Balance }[] } } };

        if (response?.data?.data) {
          setAccountBalance(response.data.data.edges[0]?.node?.value);
        }
      }
    },
    [beneficiaryAccount, currencyId, accountsList],
  );

  const getExternalAccountBalanceAction = useCallback(
    async (id?: string) => {
      const currentAccount = externalAccountsList?.find(
        (item) => item.id === pspIntegrationAccountId || id,
      );

      if (currentAccount && currencyId) {
        const response = (await getAccountBalance({
          id: currentAccount.account.id,
          currencyId: currencyId as string,
        })) as { data: { data: { edges: { node: { value: number } }[] } } };

        if (response?.data?.data) {
          setAccountBalance(response.data.data.edges[0]?.node?.value);
        }
      }
    },
    [pspIntegrationAccountId, currencyId, externalAccountsList],
  );

  const getPspPartnerAccountBalanceAction = useCallback(
    async (accountNumber?: string) => {
      const currentAccount = pspPartnersAccountsList.find(
        (item) =>
          item?.account?.fullNumber === accountNumber ||
          item?.account?.fullNumber === pspPartnerAccountNumber,
      );

      if (currentAccount) {
        const response = (await getPspPartnerAccountBalance({
          id: currentAccount.account?.id as string,
          currencyId,
        })) as { data: { data: { edges: { node: Balance }[] } } };

        if (response?.data?.data) {
          setPspPartnerAccountBalance(response.data.data?.edges[0]?.node.value);
          setPspPartnerAccountBalanceInfo(response.data.data?.edges[0]?.node);
        }
      }
    },
    [pspPartnerAccountNumber, currencyId, pspPartnersAccountsList],
  );

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    switch (name) {
      case 'pspPartnerId':
        onSearchPspPartners(searchAutocomplete);
        break;
      case 'senderAccountNumber':
        onSearchSenderAccounts(searchAutocomplete);
        break;
      case 'pspPartnerAccountNumber':
        onSearchPspPartnerAccounts(searchAutocomplete);
        break;
      case 'purposeCode':
        onSearchPurposeCodes(searchAutocomplete);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (paymentTypes && currencyId) {
      const nextPaymentTypes: SelectItemProps[] = [];

      if (transactionId) {
        paymentTypes?.forEach((item) => {
          if (
            item?.currencies?.edges?.find(
              (currency) => currency.node.id === currencyId,
            )
          ) {
            nextPaymentTypes.push({
              name: item.name,
              value: item.paymentType,
            });
          }
        });
      } else {
        paymentTypes
          ?.filter((item) => item.enabled)
          ?.forEach((item) => {
            if (
              item?.currencies?.edges?.find(
                (currency) => currency.node.id === currencyId,
              )
            ) {
              nextPaymentTypes.push({
                name: item.name,
                value: item.paymentType,
              });
            }
          });
      }

      setFilteredPaymentTypes(nextPaymentTypes);
    }
  }, [currencyId, paymentTypes]);

  useEffect(() => {
    if (
      beneficiaryAccount &&
      accountsList &&
      !transactionId &&
      !pspIntegrationAccountId
    ) {
      const currentAccount = accountsList.find(
        (item) => item.fullNumber === beneficiaryAccount,
      );

      if (currentAccount) {
        getAllAccountBalancesAction(currentAccount.id);
      }
    }
  }, [
    beneficiaryAccount,
    accountsList,
    transactionId,
    pspIntegrationAccountId,
  ]);

  useEffect(() => {
    if (pspIntegrationAccountId && externalAccountsList) {
      const currentExternal = externalAccountsList?.find(
        (item) => item.id === pspIntegrationAccountId,
      );

      if (currentExternal) {
        getAllAccountBalancesAction(currentExternal.account.id);
      }
    }
  }, [pspIntegrationAccountId, externalAccountsList]);

  useEffect(() => {
    if (currencyId && pspPartnerAccountNumber && pspPartnersAccountsList) {
      getPspPartnerAccountBalanceAction();
    }
  }, [currencyId, pspPartnerAccountNumber, pspPartnersAccountsList]);

  const onDuplicate = () => {
    onClearRequestTimer();

    dispatch(clearOutgoingTransactionState());
    dispatch(clearState());
    setUploadedFilesError([]);
    reset({});
    onClearTimer(true);

    setTimeout(() => {
      navigate(`${ROUTES.OUTGOING}${search || ''}`);
    }, 100);

    setTimeout(() => {
      navigate(`${ROUTES.OUTGOING_TRANSACTION_CREATE}${search || ''}`, {
        state: { ...transaction, duplicate: true },
      });
    }, 200);
  };

  useEffect(() => {
    if (balances && currencies && baseCurrency) {
      const currenciesList: SelectItemProps[] = [];

      if (balances?.find((item) => item.currencyId === baseCurrency.id)) {
        currenciesList.push({
          name: baseCurrency.name,
          value: baseCurrency.id,
          suffix: baseCurrency.description,
        });
      }

      currencies
        ?.filter((item) => item.value !== baseCurrency?.id)
        ?.forEach((item) => {
          if (balances.find((balance) => balance.currencyId === item.value)) {
            currenciesList.push(item);
          }
        });

      setAvailableCurrencies(currenciesList);
    }
  }, [balances, currencies, baseCurrency]);

  useEffect(() => {
    if (
      currencyId &&
      beneficiaryAccount &&
      accountsList &&
      !locationState?.duplicate
    ) {
      getAccountBalanceAction();
    }
  }, [currencyId, beneficiaryAccount, accountsList, locationState]);

  useEffect(() => {
    if (
      currencyId &&
      pspIntegrationAccountId &&
      externalAccountsList &&
      !locationState?.duplicate
    ) {
      getExternalAccountBalanceAction();
    }
  }, [currencyId, externalAccountsList, pspIntegrationAccountId]);

  useEffect(() => {
    if (
      currencyId &&
      beneficiaryAccount &&
      balances &&
      !balances?.find((item) => item.currencyId === currencyId)
    ) {
      setValue('currencyId', '');
    }
  }, [currencyId, balances, beneficiaryAccount]);

  useEffect(() => {
    if (
      fields.purposeCode &&
      !purposeCodeSettingItem &&
      !transactionId &&
      !locationState.duplicate
    ) {
      setValue('purposeCode', '');
    }
  }, [fields.purposeCode, purposeCodeSettingItem, transactionId]);

  useEffect(() => {
    if (
      currencyId &&
      prevCurrencyId &&
      currencyId !== prevCurrencyId &&
      !pspIntegrationAccountId
    ) {
      getAccountBalanceAction();
    }
  }, [currencyId, prevCurrencyId, pspIntegrationAccountId]);

  useEffect(() => {
    if (
      currencyId &&
      prevCurrencyId &&
      currencyId !== prevCurrencyId &&
      pspIntegrationAccountId
    ) {
      getExternalAccountBalanceAction();
    }
  }, [currencyId, prevCurrencyId, pspIntegrationAccountId]);

  useEffect(() => {
    if (
      transaction &&
      transaction?.pspPartnerAccountNumber &&
      pspPartnerAccounts?.length
    ) {
      const currentAccount = pspPartnerAccounts?.find(
        (item) => item.value === transaction?.pspPartnerAccountNumber,
      );

      if (!currentAccount) {
        getTransactionPspAccountByIdAction(
          transaction?.pspPartnerAccountNumber,
        );
      }
    }
  }, [transaction, pspPartnerAccounts]);

  useEffect(() => {
    if (transaction && transaction?.purposeCode && purposeCodes?.length) {
      const currentPurposeCode = purposeCodes?.find(
        (item) => item.value === transaction?.purposeCode,
      );

      if (!currentPurposeCode) {
        getPurposeCodeByCode(transaction?.purposeCode);
      }
    }
  }, [transaction, purposeCodes]);

  useEffect(() => {
    if (
      !transactionId &&
      locationState &&
      locationState.duplicate &&
      pspPartnerAccountNumber &&
      pspPartnerAccounts?.length
    ) {
      const currentAccount = pspPartnerAccounts?.find(
        (item) => item.value === pspPartnerAccountNumber,
      );

      if (!currentAccount) {
        getTransactionPspAccountByIdAction(pspPartnerAccountNumber);
      }
    }
  }, [
    locationState,
    pspPartnerAccounts,
    transactionId,
    pspPartnerAccountNumber,
  ]);

  useEffect(() => {
    if (
      !transactionId &&
      locationState &&
      locationState.duplicate &&
      locationState?.purposeCode &&
      purposeCodes?.length
    ) {
      const currentPurposeCode = purposeCodes?.find(
        (item) => item.value === locationState?.purposeCode,
      );

      if (!currentPurposeCode) {
        getPurposeCodeByCode(locationState?.purposeCode);
      }
    }
  }, [locationState, purposeCodes, transactionId]);

  useEffect(() => {
    if (transactionId) {
      dispatch(changePanelState({ state: true, transactionId }));
    } else {
      dispatch(changePanelState({ state: true }));
    }

    return () => {
      dispatch(clearOutgoingTransactionState());
      dispatch(clearState());
      dispatch(clearSignaturesState());

      dispatch(clearStatusesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});
    };
  }, [dispatch, transactionId]);

  useEffect(() => {
    if (refundIsSuccess) {
      changeStatusToRefund();
      setRefundPopupVisible(false);
    }
  }, [refundIsSuccess]);

  useEffect(() => {
    if (paymentType && paymentType !== PaymentType.SWIFT) {
      setValue('chargeType', ChargeType.SHA);
    }
  }, [paymentType]);

  useEffect(() => {
    if (transaction) {
      setSubmitDisabled(false);

      setSubmitDisabled(false);

      getAllAccountBalancesAction(transaction?.senderAccount?.id as string);

      reset({
        ...transaction,
        sum: transaction.sum ? transaction.sum.toString() : '0',
      });

      if (transaction.sum) {
        setAmount(transaction.sum.toString());
      }

      if (transaction.feeAmount) {
        setFeeValue(transaction.feeAmount);
      }

      if (transaction?.senderAccount) {
        setAccountsList([transaction?.senderAccount]);
        setAutocompleteList([
          {
            name: transaction?.senderAccount.description,
            description: '',
            value: transaction?.senderAccount.fullNumber,
            suffix: `${transaction?.senderAccount.fullNumber} ${
              // eslint-disable-next-line no-nested-ternary
              overdraftEnabled
                ? transaction?.senderAccount?.overdraftEnabled
                  ? '(Overdraft)'
                  : ''
                : ''
            }`,
          },
        ]);
      }

      if (transaction?.pspPartnerAccountNumber) {
        getPspPartnerAccountBalanceAction(transaction?.pspPartnerAccountNumber);
      }
    }
  }, [transaction]);

  useEffect(() => {
    if (
      currencyId &&
      paymentType &&
      prevCurrencyId &&
      prevPaymentType &&
      (currencyId !== prevCurrencyId || paymentType !== prevPaymentType)
    ) {
      setValue('pspPartnerId', undefined);
      setValue('pspPartnerAccountNumber', '');
    }
  }, [currencyId, prevCurrencyId, paymentType, prevPaymentType]);

  useEffect(() => {
    if (
      prevPspPartnerId &&
      prevPspPartnerId !== undefined &&
      prevPspPartnerId !== null &&
      ((pspPartnerId && pspPartnerId !== prevPspPartnerId) ||
        (prevPspPartnerId && !pspPartnerId))
    ) {
      setValue('pspPartnerAccountNumber', '');
    }
  }, [pspPartnerId, prevPspPartnerId]);

  useEffect(() => {
    if (needToCloseSidepanel) {
      dispatch(clearOutgoingTransactionState());
      dispatch(clearState());

      dispatch(clearStatusesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});
      handleClose();
      setAttachedDocumentsErrors(undefined);
      setUploadedDocumentsErrors([]);

      setTimeout(() => {
        getTransactionsList(
          `?take=${selectedCount || '25'}${search.replace('?', '&')}`,
        );
      }, 100);
    }
  }, [needToCloseSidepanel]);

  useEffect(() => {
    if (
      changeStatusIsSuccess &&
      (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
        transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
        transaction?.baseellaStatus === BaseellaStatus.REJECTED ||
        transaction?.baseellaStatus === BaseellaStatus.REFUNDED)
    ) {
      getTransactionsList(
        `?take=${selectedCount || '25'}${search.replace('?', '&')}`,
      );
    }

    if (
      (signatureIsSuccess && !transactionId) ||
      (changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED ||
          transaction?.baseellaStatus === BaseellaStatus.REFUNDED))
    ) {
      dispatch(clearOutgoingTransactionState());
      dispatch(clearState());

      dispatch(clearStatusesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});
      handleClose();
      setAttachedDocumentsErrors(undefined);
      setUploadedDocumentsErrors([]);

      if (
        changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED ||
          transaction?.baseellaStatus === BaseellaStatus.REFUNDED)
      ) {
        setTimeout(() => {
          getTransactionsList(
            `?take=${selectedCount || '25'}${search.replace('?', '&')}`,
          );
        }, 100);
      }
    }
  }, [signatureIsSuccess, transactionId]);

  useEffect(() => {
    if (transactionId && signatureIsSuccess) {
      onClearTimer(true);
      onClearRequestTimeout();
      setSignaturePopupVisible(false);

      setTimeout(() => {
        getTransactionsList(
          `?take=${selectedCount || '25'}${search.replace('?', '&')}`,
        );
      }, 100);

      getTransaction();
    }
  }, [transactionId, signatureIsSuccess]);

  useEffect(() => {
    let nextSchema: any = creationSchema;

    if (
      fields.currencyId &&
      prevCurrencyId &&
      prevCurrencyId !== fields.currencyId
    ) {
      clearErrors();
    }

    const withoutScheme = localStorage.getItem('outgoingNoScheme');
    if ((withoutScheme && withoutScheme === 'false') || !withoutScheme) {
      if (
        paymentType === PaymentType.FASTER_PAYMENT ||
        paymentType === PaymentType.BACS
      ) {
        nextSchema = {
          ...creationSchema,
          properties: {
            ...creationSchema.properties,
            // benificiaryBankSortCode: {
            //   type: 'string',
            //   regexp: sortCodeRegexp.toString(),
            //   errorMessage: 'Sort Code empty or incorrect',
            // },
            benificiaryCountry: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Country is required',
            },
            benificiaryType: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Beneficiary type is required',
            },
            recipientAccountNumber: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Account number is required',
            },
          },
          required: [
            ...creationSchema.required,
            // 'benificiaryBankSortCode',
            'recipientAccountNumber',
            'benificiaryCountry',
            'benificiaryType',
          ],
        };

        if (
          prevPaymentType &&
          (prevPaymentType === PaymentType.SWIFT ||
            prevPaymentType === PaymentType.SEPA_SCT ||
            prevPaymentType === PaymentType.SEPA_SCT_INST ||
            prevPaymentType === PaymentType.TARGET_2 ||
            prevPaymentType === PaymentType.FEDWIRE)
        ) {
          clearErrors();

          setValue('benificiaryBIC', '');
          setValue('intermediaryBankBIC', '');
          setValue('intermediaryBankCorrespondentAccount', '');
          setValue('intermediaryBankCountry', '');
          setValue('intermediaryBankAddress', '');
          setValue('intermediaryBankName', '');
          setValue('aba', '');
        }
      } else if (paymentType === PaymentType.SWIFT) {
        if (
          prevPaymentType &&
          (prevPaymentType === PaymentType.FASTER_PAYMENT ||
            prevPaymentType === PaymentType.BACS ||
            prevPaymentType === PaymentType.FEDWIRE)
        ) {
          clearErrors();

          setValue('benificiaryBankSortCode', '');
          setValue('aba', '');
        }

        nextSchema = {
          ...creationSchema,
          properties: {
            ...creationSchema.properties,
            // benificiaryBIC: {
            //   type: 'string',
            //   regexp: swiftRegexp.toString(),
            //   errorMessage: 'BIC empty or incorrect',
            // },
            recipientAccountNumber: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Account number is required',
            },
            benificiaryCountry: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Country is required',
            },
            benificiaryType: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Beneficiary type is required',
            },
          },
          required: [
            ...creationSchema.required,
            // 'benificiaryBIC',
            'recipientAccountNumber',
            'benificiaryCountry',
            'benificiaryType',
          ],
        };

        if (
          fields.intermediaryBankBIC ||
          fields.intermediaryBankCorrespondentAccount ||
          fields.intermediaryBankCountry ||
          fields.intermediaryBankAddress ||
          fields.intermediaryBankName
        ) {
          nextSchema = {
            ...creationSchema,
            properties: {
              ...creationSchema.properties,
              benificiaryCountry: {
                type: 'string',
                minLength: 1,
                errorMessage: 'Country is required',
              },
              benificiaryType: {
                type: 'string',
                minLength: 1,
                errorMessage: 'Beneficiary type is required',
              },
              // intermediaryBankBIC: {
              //   type: 'string',
              //   regexp: swiftRegexp.toString(),
              //   errorMessage: 'BIC empty or incorrect',
              // },
              // intermediaryBankCorrespondentAccount: {
              //   type: 'string',
              //   minLength: 1,
              //   errorMessage: 'Bank Correspondent Account is required',
              // },
              // intermediaryBankCountry: {
              //   type: 'string',
              //   minLength: 1,
              //   errorMessage: 'Bank Country is required',
              // },
              // intermediaryBankAddress: {
              //   type: 'string',
              //   minLength: 1,
              //   errorMessage: 'Bank Address is required',
              // },
              // intermediaryBankName: {
              //   type: 'string',
              //   minLength: 1,
              //   errorMessage: 'Bank Name is required',
              // },
            },
            required: [
              ...creationSchema.required,
              'benificiaryCountry',
              'benificiaryType',
              // 'intermediaryBankBIC',
              // 'intermediaryBankCorrespondentAccount',
              // 'intermediaryBankCountry',
              // 'intermediaryBankAddress',
              // 'intermediaryBankName',
            ],
          };
        }
      } else if (
        paymentType === PaymentType.SEPA_SCT ||
        paymentType === PaymentType.SEPA_SCT_INST ||
        paymentType === PaymentType.TARGET_2
      ) {
        if (
          prevPaymentType &&
          (prevPaymentType === PaymentType.FASTER_PAYMENT ||
            prevPaymentType === PaymentType.BACS ||
            prevPaymentType === PaymentType.SWIFT ||
            prevPaymentType === PaymentType.FEDWIRE)
        ) {
          clearErrors();

          setValue('benificiaryBankSortCode', '');
          setValue('intermediaryBankBIC', '');
          setValue('intermediaryBankCorrespondentAccount', '');
          setValue('intermediaryBankCountry', '');
          setValue('intermediaryBankAddress', '');
          setValue('intermediaryBankName', '');
          setValue('aba', '');
        }

        nextSchema = {
          ...creationSchema,
          properties: {
            ...creationSchema.properties,
            recipientAccountNumber: {
              type: 'string',
              regexp: ibanRegexp.toString(),
              errorMessage: 'IBAN empty or incorrect',
            },
            benificiaryBIC: {
              type: 'string',
              regexp: swiftRegexp.toString(),
              errorMessage: 'BIC empty or incorrect',
            },
            benificiaryCountry: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Country is required',
            },
            benificiaryType: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Beneficiary type is required',
            },
          },
          required: [
            ...creationSchema.required,
            'recipientAccountNumber',
            'benificiaryBIC',
            'benificiaryCountry',
            'benificiaryType',
          ],
        };
      } else if (paymentType === PaymentType.FEDWIRE) {
        if (
          prevPaymentType &&
          (prevPaymentType === PaymentType.FASTER_PAYMENT ||
            prevPaymentType === PaymentType.BACS ||
            prevPaymentType === PaymentType.SWIFT ||
            prevPaymentType === PaymentType.SEPA_SCT ||
            prevPaymentType === PaymentType.SEPA_SCT_INST)
        ) {
          clearErrors();

          setValue('benificiaryBankSortCode', '');
          setValue('intermediaryBankBIC', '');
          setValue('intermediaryBankCorrespondentAccount', '');
          setValue('intermediaryBankCountry', '');
          setValue('intermediaryBankAddress', '');
          setValue('intermediaryBankName', '');
          setValue('benificiaryBIC', '');
        }

        nextSchema = {
          ...creationSchema,
          properties: {
            ...creationSchema.properties,
            aba: {
              type: 'string',
              minLength: 1,
              errorMessage: 'ABA is required',
            },
          },
          required: [...creationSchema.required, 'aba'],
        };
      }
    } else {
      nextSchema = {
        type: 'object',
        properties: {
          sum: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Amount is required',
          },
          currencyId: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Currency is required',
          },
          paymentType: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Payment type is required',
          },
          // pspPartnerId: {
          //   type: 'string',
          //   minLength: 1,
          //   errorMessage: 'Bank partner is required',
          // },
          // pspPartnerAccountNumber: {
          //   type: 'string',
          //   minLength: 1,
          //   errorMessage: 'Bank partner account is required',
          // },
          senderAccountNumber: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Sender account is required',
          },
          info: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Info is required',
          },
        },
        required: [
          'sum',
          'currencyId',
          'paymentType',
          // 'pspPartnerId',
          // 'pspPartnerAccountNumber',
          'senderAccountNumber',
          'info',
        ],
      };
    }

    setFormattedSchema(nextSchema);
  }, [
    setValue,
    prevPaymentType,
    paymentType,
    prevCurrencyId,
    fields.currencyId,
    fields.intermediaryBankBIC,
    fields.intermediaryBankCorrespondentAccount,
    fields.intermediaryBankCountry,
    fields.intermediaryBankAddress,
    fields.intermediaryBankName,
  ]);

  useEffect(() => {
    if (purposeCodesSettings) {
      if (
        purposeCodeSettingSelectionType === 'country' &&
        prevBenificiaryBankCountry !== null &&
        benificiaryBankCountry &&
        prevBenificiaryBankCountry !== benificiaryBankCountry
      ) {
        if (!transactionId) {
          setValue('purposeCode', '');
          setPurposeCodeSettingItem(undefined);
        }

        const currentPurposeCodeSettings = purposeCodesSettings?.filter(
          (item) => item.bankCountry === benificiaryBankCountry,
        );

        if (currentPurposeCodeSettings?.length) {
          setPurposeCodeSettingItem(currentPurposeCodeSettings[0]);
        }
      }

      if (
        purposeCodeSettingSelectionType === 'currency' &&
        ((currencyId && prevCurrencyId && currencyId !== prevCurrencyId) ||
          (paymentType &&
            prevPaymentType !== null &&
            prevPaymentType !== paymentType))
      ) {
        if (!transactionId) {
          setValue('purposeCode', '');
          setPurposeCodeSettingItem(undefined);
        }

        const currentPaymentType = paymentTypes?.find(
          (item) => item.paymentType === fields?.paymentType,
        );

        const currentPurposeCodeSettings = purposeCodesSettings?.filter(
          (item) =>
            item.paymentCategoryId === currentPaymentType?.id &&
            item.currenciesIds.includes(fields.currencyId),
        );

        if (currentPurposeCodeSettings?.length) {
          setPurposeCodeSettingItem(currentPurposeCodeSettings[0]);
        }
      }
    }
  }, [
    currencyId,
    prevCurrencyId,
    paymentType,
    prevPaymentType,
    prevBenificiaryBankCountry,
    benificiaryBankCountry,
    purposeCodeSettingSelectionType,
    purposeCodesSettings,
    transactionId,
  ]);

  useEffect(() => {
    if (purposeCodesSettings) {
      if (
        ((fields?.currencyId && fields?.paymentType) ||
          fields?.benificiaryBankCountry) &&
        paymentTypes &&
        !purposeCodeSettingItem
      ) {
        if (!transactionId) {
          setValue('purposeCode', '');
        }

        if (fields?.currencyId && fields?.paymentType) {
          const currentPaymentType = paymentTypes?.find(
            (item) => item.paymentType === fields?.paymentType,
          );

          const currentPurposeCodeSettings = purposeCodesSettings?.filter(
            (item) =>
              item.paymentCategoryId === currentPaymentType?.id &&
              item.currenciesIds.includes(fields.currencyId),
          );

          if (currentPurposeCodeSettings?.length) {
            setPurposeCodeSettingItem(currentPurposeCodeSettings[0]);
            setPurposeCodeSettingSelectionType('currency');
          }
        }

        if (fields.benificiaryBankCountry) {
          if (!transactionId) {
            setValue('purposeCode', '');
          }

          const currentPurposeCodeSettings = purposeCodesSettings?.filter(
            (item) => item.bankCountry === fields.benificiaryBankCountry,
          );

          if (currentPurposeCodeSettings?.length) {
            setPurposeCodeSettingItem(currentPurposeCodeSettings[0]);
            setPurposeCodeSettingSelectionType('country');
          }
        }
      }
    }
  }, [
    fields?.currencyId,
    fields?.paymentType,
    fields?.benificiaryBankCountry,
    purposeCodesSettings,
    paymentTypes,
    transactionId,
  ]);

  useEffect(() => {
    if (locationState && locationState.duplicate && !fields.currencyId) {
      if (!fields.senderAccountNumber) {
        onSearchAutocomplete(
          'senderAccountNumber',
          locationState.senderAccountNumber,
        );
      }

      reset({
        senderAccountNumber: locationState.senderAccountNumber,
        sum: locationState?.sum?.toString(),
        currencyId: locationState.currencyId,
        paymentType: locationState.paymentType,
        chargeType: locationState.chargeType,
        pspPartnerId: locationState?.pspPartnerId || '',
        pspPartnerAccountNumber: locationState?.pspPartnerAccountNumber || '',
        documentNumber: locationState.documentNumber,
        info: locationState.info,
        recipientAccountNumber: locationState.recipientAccountNumber || '',
        benificiaryFullName: locationState.benificiaryFullName || '',
        benificiaryCountry: locationState.benificiaryCountry || '',
        benificiaryType: locationState.benificiaryType || '',
        benificiaryCity: locationState.benificiaryCity || '',
        benificiaryAddress: locationState.benificiaryAddress || '',
        benificiaryBIC: locationState.benificiaryBIC || '',
        benificiaryBankSortCode: locationState.benificiaryBankSortCode || '',
        benificiaryBankName: locationState.benificiaryBankName || '',
        benificiaryBankCountry: locationState.benificiaryBankCountry || '',
        benificiaryBankAddress: locationState.benificiaryBankAddress || '',
        intermediaryBankBIC: locationState.intermediaryBankBIC || '',
        intermediaryBankCorrespondentAccount:
          locationState.intermediaryBankCorrespondentAccount || '',
        intermediaryBankName: locationState.intermediaryBankName || '',
        intermediaryBankCountry: locationState.intermediaryBankCountry || '',
        intermediaryBankAddress: locationState.intermediaryBankAddress || '',
        caspId: locationState.caspId || '',
        collectorContact: locationState.collectorContact || '',
        aba: locationState?.aba || '',
        purposeCode: locationState?.purposeCode || '',
        benificiaryPostCode: locationState?.benificiaryPostCode || '',
      });

      setAmount(locationState?.sum?.toString());
    }

    if (
      locationState &&
      locationState.duplicate &&
      accountsList.length &&
      !locationState.pspInegrationAccountId
    ) {
      getAccountBalanceAction(locationState.recipientAccountNumber);
    }

    if (
      locationState &&
      locationState.duplicate &&
      externalAccountsList?.length &&
      locationState.pspInegrationAccountId
    ) {
      getExternalAccountBalanceAction(locationState.pspInegrationAccountId);
    }

    if (
      locationState &&
      locationState.duplicate &&
      locationState.recipientAccountNumber &&
      activePermissions?.includes(PermissionEnum.IBAN_VALIDATE_ACTION) &&
      (pspDetails?.find(
        (item) =>
          item.key === 'IBAN-COM-IBAN-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
      )?.data?.value as boolean) &&
      (locationState.paymentType === PaymentType.SEPA_SCT ||
        locationState.paymentType === PaymentType.SEPA_SCT_INST)
    ) {
      onValidateIban(locationState.recipientAccountNumber);
    }

    if (
      locationState &&
      locationState.duplicate &&
      locationState.benificiaryBIC &&
      activePermissions?.includes(PermissionEnum.IBAN_BIC_VALIDATE_ACTION) &&
      (pspDetails?.find(
        (item) =>
          item.key === 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
      )?.data?.value as boolean) &&
      locationState.paymentType === PaymentType.SWIFT
    ) {
      onBicValidate(locationState.benificiaryBIC, 'beneficiary');
    }
  }, [locationState, accountsList, pspDetails, externalAccountsList]);

  useEffect(() => {
    if (!currencyId && baseCurrency) {
      reset({
        ...fields,
      });
    }
  }, [currencyId, baseCurrency]);

  useEffect(() => {
    if (acceptedOut) {
      const transferDateFormatted = formatFormDate(transferDate);
      const formattedAcceptedOut = formatFormDate(acceptedOut);
      const currentDate = formatFormDate(new Date().toISOString());

      if (
        (transferDate && formattedAcceptedOut < transferDateFormatted) ||
        formattedAcceptedOut > currentDate
      ) {
        setError('acceptedOut', {
          type: 'custom',
          message:
            'Accepted Out date must be between Creation Date and current date',
        });
      } else {
        clearErrors('acceptedOut');
      }
    }
  }, [acceptedOut, transferDate]);

  useEffect(() => {
    if (transferDate) {
      const transferDateFormatted = formatFormDate(transferDate);
      const currentDate = formatFormDate(new Date().toISOString());

      if (transferDate && transferDateFormatted > currentDate) {
        setError('transferDate', {
          type: 'custom',
          message: 'Creation Date cannot be later than current date',
        });
      } else {
        clearErrors('transferDate');
      }
    }
  }, [transferDate]);

  useEffect(() => {
    if (
      !transactionId &&
      ((paymentType && !prevPaymentType) ||
        (paymentType && prevPaymentType && paymentType !== prevPaymentType))
    ) {
      getAutocompleteListAction();
    }
  }, [paymentType, prevPaymentType, transactionId]);

  useEffect(() => {
    if (transactionId && transaction && paymentType) {
      getAutocompleteListAction();
    }
  }, [transaction, transactionId, paymentType]);

  useEffect(() => {
    if (
      currencyId &&
      prevCurrencyId &&
      currencyId !== prevCurrencyId &&
      !transactionId
    ) {
      setValue('paymentType', '');
      setSubmitDisabled(true);
    }
  }, [currencyId, prevCurrencyId]);

  useEffect(() => {
    if (transactionId && isDirty) {
      onClearTimer();
    }
  }, [isDirty, transactionId]);

  useEffect(() => {
    if (!prevSignaturePopupVisible && errorMessageSignature) {
      setErrorMessageSignature('');
    }
  }, [prevSignaturePopupVisible, errorMessageSignature]);

  useEffect(() => {
    if (locationState && !locationState.duplicate) {
      setBackPath(locationState);
    }
  }, [locationState]);

  const isLoadingSidepanel = useMemo(() => {
    let res = true;

    let isLoadingSidepanelVariables =
      !isLoading && !isUninitialized && !isWarningLoading;

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_OUTGOING_EDIT_ACTION,
      )
    ) {
      isLoadingSidepanelVariables =
        isLoadingSidepanelVariables &&
        !isLoadingStatuses &&
        !isUninitializedStatuses;
    }

    if (transactionId) {
      if (isLoadingSidepanelVariables) {
        res = false;
      }
    } else {
      res = false;
    }

    return res;
  }, [
    transactionId,
    isLoading,
    isLoadingStatuses,
    isUninitializedStatuses,
    isUninitialized,
    activePermissions,
    isWarningLoading,
  ]);

  useEffect(() => {
    if (
      currentEmployee &&
      !currentEmployee.canSignTransactions &&
      createIsSuccess
    ) {
      handleClose();
      setAttachedDocumentsErrors(undefined);
      setUploadedDocumentsErrors([]);
    }
  }, [currentEmployee, createIsSuccess]);

  useEffect(() => {
    if (transaction && transaction?.pspIntegrationAccount) {
      setExternalAccountsList([transaction?.pspIntegrationAccount]);
      setExternalAccountsPagination({
        endCursor: transaction?.pspIntegrationAccount.id,
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: transaction?.pspIntegrationAccount.id,
        totalCount: 1,
      });
    }
  }, [transaction]);

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed && parsed?.noScheme && parsed?.noScheme === 'true') {
        localStorage.setItem('outgoingNoScheme', 'true');
      }
    }
  }, [search]);

  let feeText;

  if (feeValue === undefined) {
    feeText = 'Fee will be calculated after filling in the required fields';
  } else if (fields.currencyId) {
    feeText = `Fee: ${formatBalance(feeValue)} ${
      currencies?.find((item) => item.value === fields.currencyId)?.name
    }`;
  }

  const onSendCodeAction = !transactionId ? sendCodeAction : onSendCode;

  let ibanValidationPaymentTypeNotSupported = false;

  if (
    (paymentType === PaymentType.SEPA_SCT ||
      paymentType === PaymentType.SEPA_SCT_INST) &&
    !ibanValidationValues?.sepaSCT &&
    !ibanValidationValues?.sepaSCI
  ) {
    ibanValidationPaymentTypeNotSupported = true;
  } else if (
    paymentType === PaymentType.SEPA_SCT &&
    !ibanValidationValues?.sepaSCT
  ) {
    ibanValidationPaymentTypeNotSupported = true;
  } else if (
    paymentType === PaymentType.SEPA_SCT_INST &&
    !ibanValidationValues?.sepaSCI
  ) {
    ibanValidationPaymentTypeNotSupported = true;
  }

  return (
    <OutgoingSidepanel
      enableEdit
      sidepanelExpandedKey="outgoing-transaction"
      isLoading={
        isLoadingSidepanel ||
        isWarningLoading ||
        (!warnings && isWarningFetching) ||
        Boolean(!transaction && transactionId) ||
        Boolean(!warningsInfo && transactionId) ||
        isWarningInfoLoading
      }
      panelIsOpen={panelIsOpen}
      handleClose={() => {
        handleClose();
        setAttachedDocumentsErrors(undefined);
        setUploadedDocumentsErrors([]);
      }}
      title={
        transactionId ? 'Outgoing Transaction' : 'New Outgoing Transaction'
      }
      control={control as Control<any>}
      handleSubmit={onSubmit}
      isCreateForm={!transactionId}
      statusesInfo={transactionId ? statuses : undefined}
      onRollbackStatus={
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_OUTGOING_REVERSE_ACTION,
        )
          ? (status) => onChangeStatus(status)
          : undefined
      }
      onNextStatus={
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_OUTGOING_EDIT_ACTION,
        )
          ? (status) => onChangeStatus(status)
          : undefined
      }
      onChangeStatusPopup={onChangeStatusPopup}
      setStatusPopupVisible={setStatusPopupVisible}
      statusPopupVisible={statusPopupVisible}
      isDisabledSaveButton={
        !isDirty ||
        updateIsLoading ||
        createIsLoading ||
        submitDisabled ||
        sendCodeInProgress ||
        checkInProgress ||
        checkStatus === CheckTransactionStatus.RED ||
        applyTransactionsWarningIsLoading ||
        ibanValidationIsLoading ||
        Boolean(
          (pspDetails?.find(
            (item) =>
              item.key === 'IBAN-COM-IBAN-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
          )?.data?.value as boolean) &&
          (paymentType === PaymentType.SEPA_SCT ||
            paymentType === PaymentType.SEPA_SCT_INST) &&
          activePermissions?.includes(PermissionEnum.IBAN_VALIDATE_ACTION) &&
          !ibanRequestForbidden &&
          ((ibanValidationValues &&
            (!ibanValidationValues.valid ||
              ibanValidationValues?.validationErrors?.length ||
              ibanValidationPaymentTypeNotSupported)) ||
            !ibanValidationValues),
        ) ||
        Boolean(
          (pspDetails?.find(
            (item) =>
              item.key === 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
          )?.data?.value as boolean) &&
          activePermissions?.includes(
            PermissionEnum.IBAN_BIC_VALIDATE_ACTION,
          ) &&
          !bicRequestForbidden &&
          paymentType === PaymentType.SWIFT &&
          bicValidationBeneficiaryResult &&
          !bicValidationBeneficiaryResult.bicValid,
        )
      }
      transactionInfo={transaction}
      amlInfo={aml}
      amount={amount}
      createdByOrganizationId={transaction?.createdByOrganizationId}
      setAmount={setAmount}
      currencies={availableCurrencies}
      senderAccounts={senderAccounts}
      pspPartnerAccounts={pspPartnerAccounts.sort(
        (a, b) =>
          Number((a.value as string).replaceAll('.', '')) -
          Number((b.value as string).replaceAll('.', '')),
      )}
      paymentsTypes={filteredPaymentTypes}
      countries={countries}
      chargeTypes={chargeTypes}
      beneficiaryTypes={[
        {
          name: 'Individual',
          value: BenificiaryType.INDIVIDUAL,
        },
        {
          name: 'Corporate',
          value: BenificiaryType.CORPORATE,
        },
      ]}
      isEditable={
        transactionId
          ? activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_OUTGOING_EDIT_ACTION,
            )
          : activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_OUTGOING_CREATE_ACTION,
            )
      }
      createAllowed={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_OUTGOING_CREATE_ACTION,
      )}
      errors={errors && Object.keys(errors).length ? errors : undefined}
      onSearchAutocomplete={onSearchAutocomplete}
      pspPartnersIsLoading={pspPartnersIsLoading}
      senderAccountsIsLoading={senderAccountsIsLoading}
      pspPartnerAccountsIsLoading={pspPartnerAccountsIsLoading}
      paymentTypeIsFilled={Boolean(paymentType) && Boolean(currencyId)}
      pspPartnerIsFilled={Boolean(pspPartnerId)}
      pspPartners={pspPartners}
      baseellaStatus={transaction?.baseellaStatus}
      logs={statusLogs}
      paymentType={watch('paymentType')}
      feeText={feeText}
      checkError={
        statusCheckError ||
        (checkStatus === CheckTransactionStatus.RED
          ? 'Transaction could not be completed'
          : undefined)
      }
      transactionCheckError={checkError}
      employee={employee}
      onApplyTransactionAction={onApplyTransactionAction}
      warnings={warnings}
      changeLogs={transactionChangeLogs}
      accountBalance={accountBalance}
      currencyId={currencyId}
      balanceIsLoading={accountBalanceIsLoading || accountBalanceIsFetching}
      updateBalance={getAccountBalanceAction}
      updatePspPartnerBalance={getPspPartnerAccountBalanceAction}
      pspPartnerAccountBalance={pspPartnerAccountBalance}
      pspPartnerBalanceIsLoading={
        pspPartnerAccountBalanceIsLoading || pspPartnerAccountBalanceIsFetching
      }
      isDirty={isDirty}
      checkInProgress={checkInProgress}
      signaturePopupVisible={signaturePopupVisible}
      sendCode={onSendCodeAction}
      validateCode={validateCodeAction}
      transactionId={transactionId}
      validateCodeInProgress={
        validateCodeInProgress ||
        sendCodeInProgress ||
        createSignatureInProgress
      }
      signatures={signatures}
      errorSidepanel={errorMessage}
      errorMessageSignature={errorMessageSignature}
      rejectPopupVisible={rejectPopupVisible}
      updateStatusIsLoading={
        statusChangeInProgress || applyTransactionsWarningIsLoading
      }
      isLoadingStatuses={isFetchingStatuses}
      setRejectPopupVisible={setRejectPopupVisible}
      onReject={onReject}
      rejectInProgress={
        changeStatusIsLoading || applyTransactionsWarningIsLoading
      }
      transactionsTypes={transactionsTypes}
      onChangeStatusPopupLoading={updateIsLoading || changeStatusIsLoading}
      onSignatureCancelClick={onSignatureCancelClick}
      activePermission={activePermissions}
      latestQueuedStatus={transaction?.latestQueuedStatuses?.baseellaStatus}
      statusChangeError={statusChangeError}
      applyTransactionsWarningIsLoading={applyTransactionsWarningIsLoading}
      companyOfficials={companyOfficials}
      chats={chats}
      onCreateTransactionChat={onCreateTransactionChat}
      chatCreateInProgress={chatCreateInProgress}
      currentEmployee={currentEmployee}
      createChatWithMessage={createChatWithMessage}
      documents={documents}
      document={documentItem}
      documentFileIsLoading={downloadInProgress || downloadIsFetching}
      downloadInProgress={downloadInProgress || downloadIsFetching}
      imgLink={imgLink}
      onDelete={onDeleteDocument}
      deleteInProgress={deleteDocumentInProgress}
      deleteIsSuccess={deleteDocumentIsSuccess}
      onMove={onMoveDocument}
      moveInProgress={documentCategoryChangeInProgress}
      onDownload={onDownloadDocument}
      sendComment={onSendDocumentComment}
      editComment={onEditComment}
      editCommentInProgress={editCommentInProgress}
      editCommentIsSuccess={editCommentIsSuccess}
      deleteComment={onDeleteComment}
      deleteCommentInProgress={deleteCommentInProgress}
      commentInProgress={commentInProgress}
      statusChangeInProgress={documentStatusChangeInProgress}
      changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
      uploadDocumentErrors={uploadedFilesError}
      uploadDocumentInProgress={fileUploadInProgress || addDocumentInProgress}
      uploadDocumentIsSuccess={fileUploadIsSuccess}
      onUploadDocument={onUploadDocument}
      onDownloadSuccess={downloadFileIsSuccess}
      getTransactionDocument={getTransactionDocumentyIdAction}
      documentIsLoading={documentIsLoading || documentIsFetching}
      glTransactionsIsLoading={glIsLoading}
      glTransactionIsLoading={glTransactionIsLoading || glTransactionIsFetching}
      glTransactions={glTransactions}
      onGlTransactionClick={onGlTransactionClick}
      glTransaction={expandedGLTransaction}
      clearGlTransactionState={() =>
        dispatch(clearExpandedGeneralLedgerTransactionState())
      }
      expandedGLTransactionCreditBalance={expandedGLTransactionCreditBalance}
      expandedGLTransactionDebitBalance={expandedGLTransactionDebitBalance}
      expendedDebitBalanceIsLoading={
        debitBalanceIsLoading || debitBalanceIsFetching
      }
      expendedCreditBalanceIsLoading={
        creditBalanceIsLoading || creditBalanceIsFetching
      }
      deleteIsVisible={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_DOCUMENTS_DELETE_ACTION,
      )}
      deleteTransactionError={deleteTransactionError}
      onDeleteTransactions={onDeleteTransaction}
      deleteTransactionInProgress={deleteTransactionInProgress}
      updateIsLoading={updateIsLoading}
      createIsLoading={createIsLoading}
      canSignTransaction={currentEmployee?.canSignTransactions}
      deleteTransactionDisabled={
        !activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DELETE_ACTION,
        )
      }
      sendCodeInProgress={sendCodeInProgress}
      balances={balances}
      accountBalancesIsLoading={
        allAccountBalancesIsLoading || allAccountBalancesisFetching
      }
      currenciesList={dictionariesCurrencies}
      onDuplicate={onDuplicate}
      duplicate={locationState?.duplicate}
      fields={fields}
      caspList={caspList}
      getTransactionDocuments={getTransationDocumentsAction}
      documentsIsLoading={documentsIsLoading}
      getGeneralLedgerTransactionValues={
        getGeneralLedgerTransactionValuesAction
      }
      getTransactionsChats={getTransactionsChatsAction}
      transactionChatsIsLoading={transactionChatsIsLoading || cofIsLoading}
      getCompanyOfficials={getCompanyOfficialsAction}
      getSignaturesTransactionById={getSignaturesTransactionByIdAction}
      isLoadingSignatures={isLoadingSignatures}
      getOutgoingTransactionByIdAml={getOutgoingTransactionByIdAmlAction}
      isLoadingAml={isLoadingAml}
      getLogsTransactionById={getLogsTransactionByIdAction}
      isLoadingLogs={isLoadingLogs}
      overdraftEnabled={overdraftEnabled}
      senderAccountsWithInfo={accountsList}
      pspPartnersAccountsList={pspPartnersAccountsList}
      pspPartnerAccountBalanceInfo={pspPartnerAccountBalanceInfo}
      getTransactionChangeLogs={getTransactionChangeLogsAction}
      changeLogsIsLoading={changeLogsIsLoading}
      users={users}
      transactionChangeLogsPagination={transactionChangeLogsPagination}
      changeLogsIsFetching={changeLogsIsFetching}
      refundPopupVisible={refundPopupVisible}
      setRefundPopupVisible={setRefundPopupVisible}
      onChangeRefundState={onChangeRefundState}
      refundFormState={refundFormState}
      refundInProgress={refundInProgress}
      onRefund={onRefund}
      refundAmountError={refundAmountError}
      setRefundAmountError={setRefundAmountError}
      onResetTransactionWarningAction={onResetTransactionWarningAction}
      resetTransactionsWarningIsLoading={resetTransactionsWarningIsLoading}
      resetTransactionsWarningIsSuccess={resetTransactionsWarningIsSuccess}
      documentsPagination={documentsPagination}
      documentsIsFetching={documentsIsFetching}
      attachedDocumentsErrors={attachedDocumentsErrors}
      uploadedDocumentsErrors={uploadedDocumentsErrors}
      transactionComments={transactionComments}
      transactionCommentsPagination={transactionCommentsPagination}
      getTransactionCommentsAction={getTransactionCommentsAction}
      commentsIsLoading={commentsIsLoading}
      commentsIsFetching={commentsIsFetching}
      onAddCommentToTransaction={onAddCommentToTransaction}
      addCommentInProgress={addCommentInProgress}
      addCommentIsSuccess={addCommentIsSuccess}
      onEditCommentInTransaction={onEditCommentInTransaction}
      editTransactionCommentInProgress={editTransactionCommentInProgress}
      editTrancastionCommentIsSuccess={editTrancastionCommentIsSuccess}
      onDeleteCommentInTransaction={onDeleteCommentInTransaction}
      deleteCommentInTransactionInProgress={
        deleteCommentInTransactionInProgress
      }
      deleteCommentInTranscationIsSuccess={deleteCommentInTranscationIsSuccess}
      getOutgoingTransactionWarningById={
        getOutgoingTransactionWarningByIdAction
      }
      isWarningByIdFetching={isWarningByIdFetching}
      isWarningByIdLoading={isWarningByIdLoading}
      warningsInfo={warningsInfo}
      ibanValidationAvailable={Boolean(
        (pspDetails?.find(
          (item) =>
            item.key === 'IBAN-COM-IBAN-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
        )?.data?.value as boolean) &&
        activePermissions?.includes(PermissionEnum.IBAN_VALIDATE_ACTION),
      )}
      onValidateIban={onValidateIban}
      ibanValidationValues={ibanValidationValues}
      ibanValidationIsLoading={ibanValidationIsLoading}
      setValue={setValue}
      bicSearchAvailable={Boolean(
        (pspDetails?.find(
          (item) =>
            item.key === 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
        )?.data?.value as boolean) &&
        activePermissions?.includes(PermissionEnum.IBAN_BIC_SEARCH_ACTION),
      )}
      bicValidationAvailable={Boolean(
        (pspDetails?.find(
          (item) =>
            item.key === 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
        )?.data?.value as boolean) &&
        activePermissions?.includes(PermissionEnum.IBAN_BIC_VALIDATE_ACTION),
      )}
      onSearchBic={onSearchBic}
      onBicValidate={onBicValidate}
      bicSearchIsLoading={bicSearchIsLoading}
      beneficairyBicSearchResults={beneficairyBicSearchResults}
      intermediaryBicSearchResults={intermediaryBicSearchResults}
      selectedValidationType={selectedValidationType}
      bicValidationIntermediaryResult={bicValidationIntermediaryResult}
      bicValidationBeneficiaryResult={bicValidationBeneficiaryResult}
      bicValidationIsLoading={bicValidationIsLoading}
      countriesList={countriesList}
      setBicValidationBeneficiaryResult={setBicValidationBeneficiaryResult}
      setError={setError}
      clearErrors={clearErrors}
      setIbanValidationValues={setIbanValidationValues}
      selectedWarningId={selectedWarningId}
      getIntegrationLogs={getIntegrationLogs}
      integrationLogsIsLoading={integrationLogsIsLoading}
      integrationLogsIsFetching={integrationLogsIsFetching}
      integrationLogs={integrationLogs}
      integrationLogsPagination={integrationLogsPagination}
      pspPartnerAccountsNextPageIsLoading={nextPageIsLoading}
      pspPartnerAccountsOnScroll={onScroll}
      pspPartnerAccountsPagination={pagination}
      externalAccounts={externalAccountsList}
      externalAccountsIsLoading={externalAccountsListLoading}
      onSearchExternalAccounts={onSearchExternalAccounts}
      onClearExternalAccounts={onClearExxternalAccounts}
      onGetExternalAccountsListAction={getExternalAccountsListAction}
      externalAccountsPagination={externalAccountsPagination}
      externalAccountsNextPageIsLoading={externalAccountsNextPageIsLoading}
      updateExternalBalance={getExternalAccountBalanceAction}
      purposeCodeVisible={Boolean(
        activePermissions?.includes(
          PermissionEnum.PURPOSE_CODE_SETTINGS_LIST,
        ) && purposeCodeSettingItem,
      )}
      purposeCodes={purposeCodes}
      purposeCodesIsLoading={purposeCodesIsLoading}
      purposeCodesNextPageIsLoading={purposeCodesNextPageIsLoading}
      onScrollPurposeCodes={onScrollPurposeCodes}
      purposeCodesPagination={purposeCodesPagination}
      warningActionError={warningActionError}
      deleteDisabled={ffSettings?.ffDisableTxGlRemoval}
      withTime={{
        transferDateWithTime,
        acceptedOutWithTime,
      }}
      setWithTime={{
        setTransferDateWithTime,
        setAcceptedOutWithTime,
      }}
    />
  );
};

export default OutgoingTransaction;
